import { getFromDataPath, isString, reduceDataPath } from "../../utility";
import { useArsContext } from "../contexts";
import { ArsSelect } from "../inputs/ArsSelect";
import { ArsText } from "../inputs/ArsText";
import { isAnalysisMethod } from "../typeGuards";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the TemplateCodeParameterControl component.
 */
export interface TemplateCodeParameterControlProps {
  /** RFC 6901 JSON Pointer path to the TemplateCodeParameter in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing TemplateCodeParameter entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the TemplateCodeParameter in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the template code parameter editor.
 */
export const TemplateCodeParameterControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: TemplateCodeParameterControlProps): React.ReactElement => {
  const { state } = useArsContext();
  const idRoute = dataPath.replace(/\//g, "-");
  const parameterType =
    getFromDataPath(`${dataPath}/parameterType`, state, isString) ??
    // istanbul ignore next
    "string";
  const holdingAnalysisMethod = getFromDataPath(
    reduceDataPath(dataPath, 3),
    state,
    isAnalysisMethod,
  );

  /** Get other existing parameter names */
  const otherParameterNames =
    holdingAnalysisMethod?.codeTemplate?.parameters?.map((p) => p.name) ??
    // istanbul ignore next
    [];
  const thisIx = dataPath.split("/").pop();
  otherParameterNames.splice(Number(thisIx), 1); // Remove this parameter's name from the list

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <NamedObjectControl
        dataPath={dataPath}
        canRename
        disabled={disabled}
        invalidNames={otherParameterNames}
      />

      <ArsSelect
        dataPath={`${dataPath}/valueSource`}
        id={`${idRoute}-value-source`}
        label="Value source"
        labelWidth={2}
        options={[
          { value: "operation", label: "Operation" },
          { value: "groupingFactor", label: "Grouping Factor" },
        ]}
        disabled={disabled}
      />

      <ArsText
        dataPath={`${dataPath}/value`}
        id={`${idRoute}-value`}
        label="Default value(s)"
        labelWidth={2}
        placeholder="Enter one value per line"
        isArray
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />

      <ArsSelect
        dataPath={`${dataPath}/parameterType`}
        id={`${idRoute}-parameter-type`}
        label="Parameter type"
        labelWidth={2}
        disabled={disabled}
        options={[
          { value: "boolean", label: "Boolean" },
          { value: "multiSelect", label: "Multiselect" },
          { value: "number", label: "Number" },
          { value: "numberList", label: "Number List" },
          { value: "select", label: "Select" },
          { value: "string", label: "String" },
          { value: "stringList", label: "String List" },
          { value: "dataset.variable", label: "Variable" },
        ]}
      />

      {["multiSelect", "select"].includes(parameterType) && (
        <ArsText
          dataPath={`${dataPath}/availableValues`}
          id={`${idRoute}-available-values`}
          label="Available values"
          labelWidth={2}
          placeholder="Enter one value per line"
          isArray
          type="textarea"
          debounceMilliseconds={2000}
          rows={5}
          disabled={disabled}
        />
      )}
    </div>
  );
};

TemplateCodeParameterControl.displayName = "TemplateCodeParameterControl";
